import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { useAuthStore } from '@/stores/auth-store';

// ============ Types ============

export interface CompanyOverview {
  totalRecipients: number;
  totalWorkspaces: number;
  totalDomains: number;
  totalCampaigns: number;
  activeCampaigns: number;
  activeRecipients: number;
  inactiveRecipients: number;
}

export interface WorkspaceRecipients {
  workspaceId: string;
  workspaceName: string;
  recipientCount: number;
  activePercent: number;
  lastSyncDate: string | null;
}

export interface DomainRecipients {
  domain: string;
  workspaceName: string;
  recipientCount: number;
  status: string;
}

export interface CompanyRecipientsReport {
  byWorkspace: WorkspaceRecipients[];
  byDomain: DomainRecipients[];
}

export interface Campaign {
  id: string;
  name: string;
  workspaceName: string;
  status: string;
  recipientCount: number;
  startDate: string | null;
}

export interface CompanyCampaignsReport {
  campaigns: Campaign[];
}

export interface RiskLevel {
  /** critical | high | medium | low | safe — never-tested people are not a level. */
  level: string;
  count: number;
  /** Share of `totalAssessed` (measured people), 0-100. */
  percent: number;
}

export interface CompanyRiskReport {
  distribution: RiskLevel[];
  /** People whose risk has been measured (risk_status other than 'None'). */
  totalAssessed: number;
  /** People not yet measured (risk_status 'None': never tested). */
  notMeasured: number;
}

export interface Activity {
  id: string;
  type: string;
  description: string;
  workspaceName: string;
  timestamp: string;
}

export interface CompanyActivityReport {
  activities: Activity[];
}

// ============ Query Keys ============

export const companyReportKeys = {
  all: ['company-reports'] as const,
  overview: (companyId: string) => [...companyReportKeys.all, 'overview', companyId] as const,
  recipients: (companyId: string) => [...companyReportKeys.all, 'recipients', companyId] as const,
  campaigns: (companyId: string) => [...companyReportKeys.all, 'campaigns', companyId] as const,
  risk: (companyId: string) => [...companyReportKeys.all, 'risk', companyId] as const,
  activity: (companyId: string) => [...companyReportKeys.all, 'activity', companyId] as const,
};

// ============ Helpers ============

/**
 * Read companyId from the auth store.
 * Backend expects: GET /v1/companies/{companyId}/reports/{report}
 */
function useCompanyId(): string | undefined {
  return useAuthStore((state) => state.user?.companyId);
}

// ============ Hooks ============

export function useCompanyOverview() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: companyReportKeys.overview(companyId ?? ''),
    queryFn: () => fetchApi<CompanyOverview>(`/companies/${companyId}/reports/overview`),
    enabled: !!companyId,
  });
}

export function useCompanyRecipients() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: companyReportKeys.recipients(companyId ?? ''),
    queryFn: () => fetchApi<CompanyRecipientsReport>(`/companies/${companyId}/reports/recipients`),
    enabled: !!companyId,
  });
}

export function useCompanyCampaigns() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: companyReportKeys.campaigns(companyId ?? ''),
    queryFn: () => fetchApi<CompanyCampaignsReport>(`/companies/${companyId}/reports/campaigns`),
    enabled: !!companyId,
  });
}

export function useCompanyRisk() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: companyReportKeys.risk(companyId ?? ''),
    queryFn: () => fetchApi<CompanyRiskReport>(`/companies/${companyId}/reports/risk`),
    enabled: !!companyId,
  });
}

export function useCompanyActivity() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: companyReportKeys.activity(companyId ?? ''),
    queryFn: () => fetchApi<CompanyActivityReport>(`/companies/${companyId}/reports/activity`),
    enabled: !!companyId,
  });
}
